import { Injectable, inject, OnDestroy } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError, BehaviorSubject, shareReplay } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  AdGroupsResponse,
  ReportingStructureResponse,
  UpdateUserProfileRequest,
  UserDetailsResponse,
  UserProfile,
  WorksWithResponse,
} from '../models/profile-data.models';
import { PeopleApiPerson } from '../../features/dashboard/people-finder/people-finder-api.models';
import { ApiHelperService } from './api-helper.service';
import { SKIP_RETRY_INTERCEPTOR } from '../interceptors/retry.interceptor';
import { SKIP_ERROR_INTERCEPTOR } from '../interceptors/error.interceptor';
import { environment } from '../../../environments/environment';

const GRAPH_ME_URL =
  'https://graph.microsoft.com/v1.0/me?$select=id,displayName,mail,userPrincipalName,jobTitle,givenName,officeLocation,department,mobilePhone,businessPhones';

const GRAPH_PHOTO_URL = 'https://graph.microsoft.com/v1.0/me/photo/$value';

const GRAPH_WORKS_WITH =
  'https://graph.microsoft.com/v1.0/me/people?%24select=id%2CdisplayName%2CuserPrincipalName%2CjobTitle&%24filter=personType%2Fsubclass%20ne%20%27ImplicitContact%27&%24top=10';

const GRAPH_USERS_URL = 'https://graph.microsoft.com/v1.0/users';

const GET_PEOPLE_SEARCH = 'user/v1/people/search';

const GET_TOPICS = 'user/v1/topics';

const UPDATE_USER_PROFILE = 'user/v1/update-profile';

const GET_USER_DETAILS = 'user/v1/me';

const GET_BANNER = 'user/v1/banner';
const POST_BANNER = 'user/v1/user-banner';

const UPLOAD_BANNER = 'user/v1/upload-banner';
const GRAPH_LICENSE_DETAILS_URL =
  'https://graph.microsoft.com/v1.0/me/licenseDetails?\$select=skuPartNumber,servicePlans';

@Injectable({ providedIn: 'root' })
export class ProfileDataService implements OnDestroy {
  private readonly http = inject(HttpClient);

  private readonly apiHelper = inject(ApiHelperService);

  private readonly _error = new BehaviorSubject<string | null>(null);

  private userDetailsRequest$?: Observable<any>;

  /** Session cache of AD-group lookups, keyed by normalized email. */
  private readonly adGroupsCache = new Map<string, Observable<AdGroupsResponse>>();

  readonly error$ = this._error.asObservable();

  /** Fetches user profile fields from Microsoft Graph. */
  getProfile(): Observable<UserProfile> {
    return this.http
      .get<UserProfile>(GRAPH_ME_URL)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /** Fetches user Copilot access information. */
  getProfileCopilotAccessInfo(): Observable<unknown> {
    return this.http
      .get(GRAPH_LICENSE_DETAILS_URL)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /** Returns the division_short for a user email from the people search API. */
  getCurrentUserDivision(userEmail: string): Observable<string | null> {
    const email = (userEmail || '').trim();
    if (!email) return of(null);
    const context = new HttpContext()
      .set(SKIP_RETRY_INTERCEPTOR, true)
      .set(SKIP_ERROR_INTERCEPTOR, true);
    return this.http
      .get<PeopleApiPerson | PeopleApiPerson[]>(`${environment.apiBaseUrl}/${GET_PEOPLE_SEARCH}`, {
        params: { email },
        context,
      })
      .pipe(
        map((response) => {
          const person = Array.isArray(response) ? response[0] : response;
          return person?.division_short ?? null;
        }),
        catchError(() => of(null)),
      );
  }

  /** Fetches another user's profile fields from the intranet people search API. */
  getUserProfile(userEmail: string): Observable<UserProfile> {
    const email = (userEmail || '').trim();
    const context = new HttpContext()
      .set(SKIP_RETRY_INTERCEPTOR, true)
      .set(SKIP_ERROR_INTERCEPTOR, true);

    return this.http
      .get<PeopleApiPerson | PeopleApiPerson[]>(`${environment.apiBaseUrl}/${GET_PEOPLE_SEARCH}`, {
        params: { email },
        context,
      })
      .pipe(
        map((response) => {
          const person = Array.isArray(response) ? response[0] : response;
          if (!person) {
            throw new Error('Person not found.');
          }
          return this.toPeopleUserProfile(person);
        }),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  private toPeopleUserProfile(person: PeopleApiPerson): UserProfile {
    const displayName = [person.firstname, person.lastname].filter(Boolean).join(' ');
    const photoDataUrl = person.photo?.trim()
      ? `data:image/jpeg;base64,${person.photo.trim()}`
      : undefined;
    return {
      id: person.employee_id,
      displayName,
      mail: person.email ?? '',
      userPrincipalName: person.email ?? '',
      jobTitle: person.title ?? null,
      givenName: person.firstname,
      officeLocation: person.room ?? person.location ?? null,
      department: person.department ?? null,
      mobilePhone: person.phone ?? '',
      businessPhones: person.phone ? [person.phone] : [],
      personnelType: person.personnel_type ?? null,
      staffCategory: person.sub_type ?? null,
      adminAssistant: person.admin_assistant
        ? [
            person.admin_assistant.first_name,
            person.admin_assistant.middle_name,
            person.admin_assistant.last_name,
          ]
            .filter(Boolean)
            .join(' ') || null
        : null,
      division_short: person.division_short ?? null,
      photoDataUrl,
    };
  }

  /** Fetches people the user works with from Microsoft Graph. */
  getWorksWith(): Observable<WorksWithResponse> {
    const context = new HttpContext()
      .set(SKIP_RETRY_INTERCEPTOR, true)
      .set(SKIP_ERROR_INTERCEPTOR, true);
    return this.apiHelper.get<WorksWithResponse>(GRAPH_WORKS_WITH, {
      headers: { ConsistencyLevel: 'eventual' },
      context,
    });
  }

  /** Fetches people a redirected user works with from Microsoft Graph. */
  getWorksWithForUser(userEmail: string): Observable<WorksWithResponse> {
    const email = encodeURIComponent((userEmail || '').trim());
    const url =
      `${GRAPH_USERS_URL}/${email}/people` +
      '?$select=id,displayName,userPrincipalName,jobTitle' +
      "&$filter=personType/subclass ne 'ImplicitContact'" +
      '&$top=10';

    const context = new HttpContext()
      .set(SKIP_RETRY_INTERCEPTOR, true)
      .set(SKIP_ERROR_INTERCEPTOR, true);
    return this.apiHelper.get<WorksWithResponse>(url, {
      headers: { ConsistencyLevel: 'eventual' },
      context,
    });
  }

  /**
   * Fetches the AD groups a user is a transitive member of from Microsoft Graph.
   *
   * The result is cached per email for the session, since the platform-access
   * guard calls this on every activation of guarded routes. The cache is
   * cleared on error so a transient failure is never cached, and can be reset
   * explicitly via {@link clearUserADGroupsCache} (e.g. on logout).
   */
  getUserADGroups(userEmail: string): Observable<AdGroupsResponse> {
    const key = (userEmail || '').trim().toLowerCase();

    const cached = this.adGroupsCache.get(key);
    if (cached) {
      return cached;
    }

    const email = encodeURIComponent((userEmail || '').trim());
    const url =
      `${GRAPH_USERS_URL}/${email}/transitiveMemberOf/microsoft.graph.group` +
      '?$select=id,displayName';

    const context = new HttpContext()
      .set(SKIP_RETRY_INTERCEPTOR, true)
      .set(SKIP_ERROR_INTERCEPTOR, true);

    const request$ = this.apiHelper.get<AdGroupsResponse>(url, { context }).pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError((err: HttpErrorResponse) => {
        this.adGroupsCache.delete(key);
        return throwError(() => err);
      }),
    );

    this.adGroupsCache.set(key, request$);
    return request$;
  }

  /** Clears all cached AD-group lookups (e.g. on logout). */
  clearUserADGroupsCache(): void {
    this.adGroupsCache.clear();
  }

  /** Fetches the reporting chain for a user from Microsoft Graph. */
  getReportingStructure(userEmail: string): Observable<ReportingStructureResponse> {
    const email = encodeURIComponent((userEmail || '').trim());

    const url =
      `${GRAPH_USERS_URL}/${email}` +
      '?$select=id,displayName,mail,jobTitle' +
      '&$expand=manager($levels=max;$select=id,displayName,mail,jobTitle)';

    const context = new HttpContext()
      .set(SKIP_RETRY_INTERCEPTOR, true)
      .set(SKIP_ERROR_INTERCEPTOR, true);
    return this.apiHelper.get<ReportingStructureResponse>(url, {
      headers: { ConsistencyLevel: 'eventual' },
      context,
    });
  }

  /** Fetches topics which user has selected. */
  getTopics(): Observable<any> {
    return this.http
      .get(`${environment.apiBaseUrl}/${GET_TOPICS}`)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /** Fetches user details from the intranet API. */
  getUserDetails(): Observable<any> {
    if (!this.userDetailsRequest$) {
      const context = new HttpContext()
        .set(SKIP_RETRY_INTERCEPTOR, true)
        .set(SKIP_ERROR_INTERCEPTOR, true);
      this.userDetailsRequest$ = this.http
        .get(`${environment.apiBaseUrl}/${GET_USER_DETAILS}`, { context })
        .pipe(
          shareReplay({ bufferSize: 1, refCount: true }),
          catchError((err: HttpErrorResponse) => {
            this.userDetailsRequest$ = undefined;
            return throwError(() => err);
          }),
        );
    }

    return this.userDetailsRequest$;
  }

  /** Clears the cached user details and fetches fresh data from the API. */
  refreshUserDetails(): Observable<any> {
    this.userDetailsRequest$ = undefined;
    return this.getUserDetails();
  }

  /** Fetches the employee's profile banner URL. Returns null when no banner is set. */
  getBanner(): Observable<string | null> {
    const context = new HttpContext()
      .set(SKIP_RETRY_INTERCEPTOR, true)
      .set(SKIP_ERROR_INTERCEPTOR, true);

    return this.http
      .get(`${environment.apiBaseUrl}/${GET_BANNER}`, { responseType: 'blob', context })
      .pipe(
        map((blob) => URL.createObjectURL(blob)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            return of(null);
          }
          return this.handleError(err);
        }),
      );
  }

  /** Fetches a redirected employee's profile banner URL by posting their email id. */
  getBannerForUser(emailId: string): Observable<string | null> {
    const normalizedEmailId = emailId.trim();
    if (!normalizedEmailId) {
      return of(null);
    }

    const context = new HttpContext()
      .set(SKIP_RETRY_INTERCEPTOR, true)
      .set(SKIP_ERROR_INTERCEPTOR, true);

    return this.http
      .post(
        `${environment.apiBaseUrl}/${POST_BANNER}`,
        { email: normalizedEmailId },
        { responseType: 'blob', context },
      )
      .pipe(
        map((blob) => URL.createObjectURL(blob)),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            return of(null);
          }
          return this.handleError(err);
        }),
      );
  }

  /** Uploads a new profile banner image for the logged-in employee. */
  uploadBanner(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('banner', file);
    return this.http
      .post<{ banner: string | null }>(`${environment.apiBaseUrl}/${UPLOAD_BANNER}`, formData)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /** Updates user profile details, including onboarding completion status. */
  updateUserProfile(payload: UpdateUserProfileRequest): Observable<UserDetailsResponse> {
    return this.http
      .put<UserDetailsResponse>(`${environment.apiBaseUrl}/${UPDATE_USER_PROFILE}`, payload)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * Fetches the user's profile photo as a Blob and converts it to an
   * object URL. Returns null when no photo is set (404) so callers can
   * fall back to initials / placeholder without treating it as an error.
   */
  getProfilePhoto(): Observable<string | null> {
    return this.http.get(GRAPH_PHOTO_URL, { responseType: 'blob' }).pipe(
      map((blob) => URL.createObjectURL(blob)),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404 || err.status === 401) {
          return of(null);
        }
        // Graph returns non-404 with error.code "ImageNotFound" when no photo is set
        if (this.isImageNotFound(err)) {
          return of(null);
        }
        return this.handleError(err);
      }),
    );
  }

  /** Fetches another user's profile photo from Microsoft Graph. */
  getUserProfilePhoto(userIdOrUpn: string): Observable<string | null> {
    const identifier = (userIdOrUpn || '').trim();
    if (!identifier) {
      return of(null);
    }

    const url = `${GRAPH_USERS_URL}/${encodeURIComponent(identifier)}/photo/$value`;
    const context = new HttpContext()
      .set(SKIP_RETRY_INTERCEPTOR, true)
      .set(SKIP_ERROR_INTERCEPTOR, true);

    return this.http.get(url, { responseType: 'blob', context }).pipe(
      map((blob) => URL.createObjectURL(blob)),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404 || err.status === 401) {
          return of(null);
        }
        if (this.isImageNotFound(err)) {
          return of(null);
        }
        return this.handleError(err);
      }),
    );
  }

  private isImageNotFound(err: HttpErrorResponse): boolean {
    try {
      const body = typeof err.error === 'string' ? JSON.parse(err.error) : err.error;
      return body?.error?.code === 'ImageNotFound';
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    this._error.complete();
  }

  private handleError(err: HttpErrorResponse): Observable<never> {
    return throwError(() => new Error(this.errorMessage(err)));
  }

  private errorMessage(err: HttpErrorResponse): string {
    if (err.status === 0) {
      return 'Network error — please check your connection.';
    }
    if (err.status === 401) {
      return 'Unauthorised — please sign in again.';
    }
    if (err.status === 403) {
      return 'Access denied — missing required Graph API permissions.';
    }
    if (err.status === 429) {
      return 'Too many requests — please try again shortly.';
    }
    if (err.status >= 500) {
      return 'Microsoft Graph service error — please try again later.';
    }
    return err.message ?? 'An unexpected error occurred.';
  }
}
